export interface WebSocketMessage {
    type: string;
    data: any;
    timestamp?: number;
}

export interface AutomationProgress {
    progress: number;
    stage: string;
    message: string;
}

export interface WebSocketCallbacks {
    onConnect?: () => void;
    onDisconnect?: () => void;
    onError?: (error: any) => void;
    onSuccess?: (result: any) => void;
    onMessage?: (message: WebSocketMessage) => void;
}

class WebSocketService {
    private ws: WebSocket | null = null;
    private clientId: string | null = null;
    private callbacks: WebSocketCallbacks = {};
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000;
    private isConnecting = false;

    constructor(private baseUrl: string) {}

    setCallbacks(callbacks: WebSocketCallbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }

    connect(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }

            if (this.isConnecting) {
                reject(new Error("Connection already in progress"));
                return;
            }

            this.isConnecting = true;
            // Use the correct WebSocket URL format as shown in the reference
            const wsUrl =
                this.baseUrl
                    .replace("http://", "ws://")
                    .replace("https://", "wss://") + "/ws";

            try {
                this.ws = new WebSocket(wsUrl);

                this.ws.onopen = () => {
                    console.log("WebSocket connected");
                    this.isConnecting = false;
                    this.reconnectAttempts = 0;
                    this.callbacks.onConnect?.();
                    resolve();
                };

                this.ws.onmessage = (event) => {
                    try {
                        const message: WebSocketMessage = JSON.parse(
                            event.data,
                        );
                        this.handleMessage(message);
                    } catch (error) {
                        console.error(
                            "Failed to parse WebSocket message:",
                            error,
                        );
                    }
                };

                this.ws.onclose = (event) => {
                    console.log(
                        "WebSocket disconnected:",
                        event.code,
                        event.reason,
                    );
                    this.isConnecting = false;
                    this.callbacks.onDisconnect?.();

                    // Attempt to reconnect if not a normal closure
                    if (
                        event.code !== 1000 &&
                        this.reconnectAttempts < this.maxReconnectAttempts
                    ) {
                        this.attemptReconnect();
                    }
                };

                this.ws.onerror = (error) => {
                    console.error("WebSocket error:", error);
                    this.isConnecting = false;
                    this.callbacks.onError?.(error);
                    reject(error);
                };
            } catch (error) {
                this.isConnecting = false;
                reject(error);
            }
        });
    }

    private attemptReconnect() {
        this.reconnectAttempts++;
        const delay =
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

        console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
        );

        setTimeout(() => {
            this.connect().catch((error) => {
                console.error("Reconnection failed:", error);
            });
        }, delay);
    }

    private handleMessage(message: WebSocketMessage) {
        this.callbacks.onMessage?.(message);

        switch (message.type) {
            case "connection":
                this.clientId = message.data.clientId;
                console.log("Assigned Client ID:", this.clientId);
                break;

            case "success":
                this.callbacks.onSuccess?.(message.data);
                break;

            case "error":
                this.callbacks.onError?.(message.data);
                break;

            case "pong":
                console.log("Received pong response");
                break;

            default:
                console.log("Unknown message type:", message.type);
        }
    }

    disconnect() {
        if (this.ws) {
            this.ws.close(1000, "Client disconnect");
            this.ws = null;
        }
        this.clientId = null;
        this.reconnectAttempts = 0;
    }

    send(message: WebSocketMessage) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            const messageWithTimestamp = {
                ...message,
                timestamp: Date.now(),
            };
            this.ws.send(JSON.stringify(messageWithTimestamp));
        } else {
            throw new Error("WebSocket is not connected");
        }
    }

    ping() {
        this.send({
            type: "ping",
            data: {},
        });
    }

    startAutomation(data: any) {
        this.send({
            type: "evaluationAndFeedbackAutomation",
            data: data,
        });
    }

    isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    getClientId(): string | null {
        return this.clientId;
    }
}

// Create a singleton instance
const websocketService = new WebSocketService(
    // process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT || 'http://localhost:3000'
    process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT ||
        "http://localhost:3000",
);

export default websocketService;
